/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'

import {
  SvgIcon,
  Button,
  PageHeader,
  Spinner,
  CardTable as WorkflowStepTable,
  NavLink
} from '../'

import { WorkflowStepsTableDataRow } from './WorkflowStepsTableDataRow'

import {
  decodeBase64,
  getNodeTypeFromBase64Id,
  hasResponsibility
} from '../../utils'

const getCurrentWorkflowRunStep = (currentUrl) => {
  let path = currentUrl.split('/').pop()
  if (getNodeTypeFromBase64Id(path) === 'WorkflowRunStepNode') {
    return path
  } else {
    return null
  }
}


class WorkflowRunSteps extends React.PureComponent {
  static propTypes = {
    title: PropTypes.string,
    workflowRunId: PropTypes.string.isRequired,
    hideActionButtons: PropTypes.bool,
    noStepExpand: PropTypes.bool,
    className: PropTypes.string
  }

  static defaultProps = {
    title: 'Workflow steps',
    hideActionButtons: false,
    noStepExpand: false
  }

  state = {
    workflowRunStepExpanded: getCurrentWorkflowRunStep(this.props.location.pathname)
  }

  generateTableDataRows = (dataRows) => {
    let rows = dataRows.map(row => (
      <WorkflowStepsTableDataRow
        workflowRunStep={row.node}
        expanded={this.state.workflowRunStepExpanded === row.node.id}
        noExpand={this.props.noStepExpand}
        onClick={() => this.props.noStepExpand
          ? null
          : this.expandRow(row.node.id)}
        key={row.node.id}
      />
    ))

    return rows
  }

  expandRow = (workflowRunStepId) => (
    this.setState(prevState => (
      {
        workflowRunStepExpanded: prevState.workflowRunStepExpanded === workflowRunStepId
          ? null
          : workflowRunStepId
      }
    ),
    () => (
      this.props.history.push(`/os/operations/general/workflows/${this.props.workflowRunId}/${workflowRunStepId}`))
    )
  )

  reset = () => {
    let data = new FormData()
    data.append("workflowrun", decodeBase64(this.props.workflowRunId))

    fetch(`${process.env.REACT_APP_API_ROOT}/v1/workflowrunreset/`,
    {
      method: "POST",
      headers: {
        Authorization: "Token " + localStorage.getItem('token') || null
      },
      body: data
    })
    .then(res => {
      res.json()
      .then(data => {
        if(data.reset) {
          console.log('reset success')
          this.props.data.refetch()
        } else {
          console.log(`reset failed - ${data}`)
        }
      })
    })
  }

  skip = () => {
    let data = new FormData()
    data.append("workflowrun", decodeBase64(this.props.workflowRunId))

    fetch(`${process.env.REACT_APP_API_ROOT}/v1/workflowrunskip/`,
    {
      method: "POST",
      headers: {
        Authorization: "Token " + localStorage.getItem('token') || null
      },
      body: data
    })
    .then((res) => res.json())
    .then((data) => {
      if(data.skipped_workflowrun) {
        console.log('skip success')
        this.props.data.refetch()
      } else {
        console.log(`skip failed - ${data}`)
      }
    })
  }

  userHasResetPermission = (workflowRunHasRequestRuns, workflowRunAtStep) => hasResponsibility('self', 'workflowrun_reset', 'workflowrun', null) ||
    (workflowRunHasRequestRuns && workflowRunAtStep === 2 && JSON.parse(localStorage.getItem('isUserPicsaEmployee')))
  userHasSkipPermission = () => hasResponsibility('self', 'workflowrun_markpendingskipped', 'workflowrun', null)

  getRequestPageLink = (workflowRun) => {
    const requestRun = workflowRun.requestruns.edges[0].node

    if (requestRun.identity) {
      return `/os/identity/${requestRun.identity.id}/requests/${requestRun.id}`
    } else {
      return `/os/operations/general/requests/${requestRun.id}`
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner />
    }

    if (this.props.data.error) {
      return <h1>Oh dear! We have an issue</h1>
    }

    const workflowRunSteps = this.props.data.workflowrunsteps
    const rows = workflowRunSteps
      ? this.generateTableDataRows(workflowRunSteps.edges)
      : null

    const workflowRun = workflowRunSteps
      ? workflowRunSteps.edges[0].node.workflowrun
      : null

    const workflowRunAtStep = workflowRunSteps && workflowRunSteps.edges.length > 0
      ? workflowRun.atStep
      : null

    const workflowRunHasRequestRuns = workflowRunSteps && workflowRunSteps.edges.length > 0
      ? workflowRun.requestruns.edges.length > 0
      : false

    let actionButtons = [
      <Button
        type='brand'
        onClick={this.reset}
        minWidth={120}
        key='Reset'
        disabled={!this.userHasResetPermission(workflowRunHasRequestRuns, workflowRunAtStep)}>
        Reset
      </Button>,
      <Button
        type='brand'
        onClick={this.skip}
        minWidth={120}
        key='Skip'
        disabled={!this.userHasSkipPermission()}>
        Mark Pending Skipped
      </Button>
    ]

    return (
      <section className={`stage slds-nowrap ${this.props.className ? this.props.className : ''}`}>
        <PageHeader name={`${this.props.title}:`} />
        <div className='stage-main slds-grid slds-wrap slds-grow slds-scrollable--y' role="main">
          <div
            className='slds-grow slds-size--1-of-1 slds-medium-size--1-of-2 slds-large-size--8-of-12 slds-col-rule--right'
            style={{ padding: '1%' }}>
            {this.props.workflowRunId
              ? <React.Fragment>
                  <div className='slds-grid'>
                    <div className='slds-col_bump-left'>
                      <Button
                        onClick={() => this.props.data.refetch()}
                        minWidth={120}>
                        <SvgIcon
                          variant='utility'
                          symbol='refresh'
                          size='x-small'
                          buttonIcon={true}
                          className='slds-button__icon_left'
                        />
                        Refresh
                      </Button>
                      {!this.props.hideActionButtons &&
                        actionButtons
                      }
                    </div>
                  </div>
                  <WorkflowStepTable
                    heading={
                      <React.Fragment>
                        {workflowRunHasRequestRuns
                          ? <NavLink
                              to={this.getRequestPageLink(workflowRun)}>
                              {workflowRun.workflow.name}
                            </NavLink>
                          : workflowRun.workflow.name
                        }
                        <br />
                        <div className='u-font-size-smaller'>
                          {workflowRun.description}
                        </div>
                      </React.Fragment>
                    }
                    headingIcon="task"
                    columns={[
                      "Name",
                      "Status",
                      // "Assigned to",
                      // "Scheduled at",
                      "Updated at"
                    ]}
                    tableDataRows={rows}
                    navigationButtons={false}
                  />
                </React.Fragment>
              : <React.Fragment>
                  <div className='slds-grid'>
                    <div className='slds-col_bump-left'>
                      <Button
                        onClick={() => this.props.data.refetch()}
                        minWidth={120}>
                        <SvgIcon
                          variant='utility'
                          symbol='refresh'
                          size='x-small'
                          buttonIcon={true}
                          className='slds-button__icon_left'
                        />
                        Refresh
                      </Button>
                    </div>
                  </div>
                  <table>
                    <tr className='u-font-size-larger'>
                      <SvgIcon
                        variant='utility'
                        symbol='warning'
                        colour='error'
                        size='small'
                        className='slds-m-right_small'
                      />
                      No workflow found.
                    </tr>
                  </table>
                </React.Fragment>
            }
          </div>
        </div>
      </section>
    )
  }
}

export const getWorkflowRunSteps = gql`
  query($workflowRun: UUID, $noWorkflowRunIdProvided: Boolean!) {
    workflowrunsteps(workflowrun_Id: $workflowRun, orderBy: "workflowstep__number") @skip(if: $noWorkflowRunIdProvided) {
      edges {
        node {
          ...WorkflowRunStep
        }
      }
    }
  }
  ${fragments.workflowRunStep}
`

const withData = graphql(getWorkflowRunSteps, {
  options: ({ workflowRunId }) => ({
    variables: {
      workflowRun: workflowRunId ? decodeBase64(workflowRunId) : null,
      noWorkflowRunIdProvided: workflowRunId === null
    },
    fetchPolicy: 'network-only'
  })
})

export default compose(
  withData,
  withRouter
)(WorkflowRunSteps)



// WEBPACK FOOTER //
// ./src/components/workflow-run-steps/index.js